/**
 * Cropping a chosen image to a square before it is uploaded.
 *
 * Done here rather than on the server for two reasons. An avatar is rendered in a circle at
 * 24px and never larger than 64, so shipping the original is sending a four-megapixel
 * photograph to draw a thumbnail — and cropping first turns a 4MB phone picture into about
 * forty kilobytes, which puts the size limit out of reach rather than in the way.
 *
 * The crop is centred, which is the right default for the overwhelmingly common case of a
 * portrait, and it is a DEFAULT rather than the only option — the caller can offer a picker
 * later without this changing.
 */

/** 512 is four times the largest place an avatar is drawn, so it stays sharp on a retina screen. */
const OUTPUT_PX = 512

/**
 * Which square of the source is kept, and how big the result is.
 *
 * Pure, and separate from the drawing, so the arithmetic can be tested without a canvas —
 * every interesting case here (landscape, portrait, an image smaller than the target) is a
 * question about four numbers.
 */
export function squareCropBox(
  width: number,
  height: number,
  size: number,
): { sx: number; sy: number; edge: number; out: number } {
  // The largest square that fits, taken from the middle.
  const edge = Math.min(width, height)
  return {
    sx: (width - edge) / 2,
    sy: (height - edge) / 2,
    edge,
    // Never upscale: a 200px photo stays 200px rather than being blown up to 512 and
    // looking worse than the original did.
    out: Math.min(size, edge),
  }
}

export interface CroppedImage {
  blob: Blob
  /** For an instant preview, before anything is uploaded. */
  previewUrl: string
  width: number
  height: number
}

/**
 * Centre-crops to a square and re-encodes as JPEG.
 *
 * JPEG rather than the original type on purpose: a photo is what people upload, PNG carries
 * it at several times the size for no visible gain at this scale, and a fixed output type
 * means the upload's content type is known before the file is read.
 */
export async function cropToSquare(file: File, size = OUTPUT_PX): Promise<CroppedImage> {
  const bitmap = await loadBitmap(file)
  try {
    const { sx, sy, edge, out } = squareCropBox(bitmap.width, bitmap.height, size)

    const canvas = document.createElement('canvas')
    canvas.width = out
    canvas.height = out
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not read that image.')
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, sx, sy, edge, edge, 0, 0, out, out)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    )
    if (!blob) throw new Error('Could not process that image.')

    return { blob, previewUrl: canvas.toDataURL('image/jpeg', 0.8), width: out, height: out }
  } finally {
    // `createImageBitmap` holds decoded pixels; a few of these is tens of megabytes.
    bitmap.close?.()
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap> {
  if ('createImageBitmap' in window) {
    try {
      // Honours EXIF orientation, so a phone photo is not silently rotated.
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Fall through: some browsers reject the options bag rather than ignoring it.
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('That file is not an image this browser can read.'))
      img.src = url
    })
    return await createImageBitmap(img)
  } finally {
    URL.revokeObjectURL(url)
  }
}
